import type {
	PageInfo,
	ResultsSort,
	SearchPageEdge,
	SearchPageResults,
} from '@atlassian/search-client';
import type { BackendExperiment } from '@atlassian/search-experiment';

import type { QueryParams } from '../types';
import type { ResultsFetchError } from '../utils/fetch-error';

import type { ApplicationModes } from './application-modes';
import type { CloudConfig } from './filters/cloud-config/types';
import type { AllFilterStates, AllFilterValues } from './filters/types';
import type { PrimaryProductKey } from './products';
import type {
	ThirdPartyConfigsBootstrap,
	ThirdPartyConfigsWithAPIResponse,
} from './schemas/3p-config';
import type { ProductKeys, SearchQueryStringParams } from './schemas/query-params';

export type ProductResultCount = {
	[key: string]: number;
};

export const SearchExperience = {
	QUICK_FIND: 'quick-find',
	SEARCH_PAGE: 'search-page',
} as const;

export type SearchStoreExperience = (typeof SearchExperience)[keyof typeof SearchExperience];

export type SearchStoreContainerType<TFilter> = {
	queryParams: QueryParams;
	allFilters: Record<string, TFilter>;
	config: CloudConfig;
	setQueryParams: (_: Partial<SearchQueryStringParams>) => void;
	availableProducts: ProductKeys[];
	allFilterExcludedProducts: ProductKeys[];
	applicationMode: ApplicationModes;
	primaryProduct?: PrimaryProductKey;
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
	experience: SearchStoreExperience;
};

export type SearchStateFilters = Record<string, AllFilterStates | undefined>;

export type PaginationToken = {
	cursor: string;
};

export type SearchStoreContainerProps = SearchStoreContainerType<AllFilterValues>;

export type SearchState = {
	retryCount: number;
	queryVersion: number;
	abTest: SearchPageResults['abTest'] | undefined;
	queryUpdatedTime: number | undefined;
	resultsSort: ResultsSort;
	isLoading: boolean;
	results: SearchPageResults | undefined;
	shownCount: number;

	// Errors within the GraphQL response object such as downstream errors
	// OR if the GraphQL request fails.
	resultError: ResultsFetchError | undefined;

	requestId: string | undefined;
	traceId: string | undefined;
	productResultCountMap: ProductResultCount | null;
	isJiraHydrationLoading: boolean | undefined;
	isCompassTeamsLoading: boolean | undefined;

	// Search Input states
	inputQuery: string;
	searchQuery: string;
	originalQuery: string | undefined;

	// Product states
	availableProducts: ProductKeys[];
	primaryProduct: PrimaryProductKey | null;

	// Products excluded from query by all products (.e.g. Can only search via specific product filter)
	allFilterExcludedProducts: ProductKeys[];

	// Filter States
	filters: Record<string, AllFilterStates | undefined>;
	filterSelectionOrder: string[];
	selectedProducts: ProductKeys[];
	selectedDatasource?: ThirdPartyConfigsWithAPIResponse;

	// Auto correction
	autoCorrection: string | undefined;

	// Pagination
	// This is not multi-product aware, but is a single cursor for all products
	// However, this structure is one step towards multi product pagination
	paginationTokens: PaginationToken[];
	isLoadingMore: boolean;

	// CQL support: CQL only support when it's the only parameter in the query
	cql: string | undefined;

	// leap query
	leapQuery: {
		loading: boolean;
		candidates: string[];
		titleIndex: number;
	};

	product: string | undefined;

	requestedBackendExperiments: BackendExperiment | undefined;

	/**
	 * A set of ids supplied to jiraFilters.issueFilter.issueTypeIDs powered by SearchPageJiraIssueTypeQuery
	 * This maps an id set at site level to the humanised type for "Incident", e.g. in Hello the JSM type=Incident is [`29160`]
	 * We only need this when filtering to the Incident type in JSM and we have to run a query to get it, defaults to []
	 */
	jsmIncidentFilterIds: string[];

	// Whether the filter dropdown is open by default
	filterDropdownOpenByQueryParam: boolean;

	// Search Page or Quick Find
	experience: SearchStoreExperience;
};

export type AppendResultsArgs = { edges: SearchPageEdge[]; pageInfo: PageInfo } | undefined;
